import type { IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createHeaders, fetchJsonQuery } from "@mne-core/api";

import AuthenticationURI from "./AuthenticationURIBuilder";

export interface IRequestRedirectURIByProviderIDCriteria {
  provider: string; // ID or shortname of OpenID provider
  state?: string; // State to retain
  origin?: string; // Origin header
}

export interface IRequestRedirectURIByProviderIDOpts extends RequestInit {
  apiUrl: string;
  apikey?: string;
  ctrl?: QueryController;
}

export interface IRequestRedirectURIByProviderIDResponse {
  providerURI: string; // URI that the client should redirect to for authentication
  nonce: string; // Random nonce which is also a part of the providerURI
}

export const createRequest = (
  criteria: IRequestRedirectURIByProviderIDCriteria,
  opts: IRequestRedirectURIByProviderIDOpts,
): Request => {
  const { apiUrl, apikey, ...restOpts } = opts;
  const { provider, state, origin } = criteria;
  const uri = new AuthenticationURI()
    .setApiUrl(apiUrl)
    .setSegment(["openid", "provider", provider, "authentication"])
    .setQuery({ state })
    .build();

  const headers = createHeaders({
    /* If the apikey is something else then undefined its added */
    "ARGUS-API-KEY": apikey,
    Accept: "application/json",
    "Content-type": "application/json",
    Origin: origin,
  });

  let localRequestOpts: RequestInit = {
    headers,
    method: "GET",
    mode: "cors",
  };

  if (!apikey) {
    /**
     * Only add cookies if no apikey is provided
     */
    localRequestOpts = {
      ...localRequestOpts,
      credentials: "include",
    };
  }

  return new Request(String(uri), {
    ...localRequestOpts,
    ...restOpts,
  });
};

async function requestRedirectURIByProviderID(
  criteria: IRequestRedirectURIByProviderIDCriteria,
  opts: IRequestRedirectURIByProviderIDOpts,
): Promise<JsonQuery<IResultstash<IRequestRedirectURIByProviderIDResponse>>> {
  return await fetchJsonQuery<IResultstash<IRequestRedirectURIByProviderIDResponse>>(createRequest(criteria, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default requestRedirectURIByProviderID;
